"use client";

import { useAuth } from "@clerk/nextjs";
import { useUserLGSs } from "modules/lgs/hooks/useUserLGSs";
import { ChevronRight, Store } from "lucide-react";
import Link from "next/link";

interface CityLockedStoreCTAProps {
  /** wotcIds of nearby stores (server-fetched) */
  nearbyStoreIds: number[];
  browseStoresUrl: string;
}

/**
 * Client component that conditionally renders a "Join a store nearby" card
 * on the locked city screen. Only shows when the user is authenticated and
 * hasn't joined any stores in the area.
 */
export function CityLockedStoreCTA({
  nearbyStoreIds,
  browseStoresUrl,
}: CityLockedStoreCTAProps) {
  const { userId } = useAuth();
  const { storeIds, isInitialLoading } = useUserLGSs(userId ?? "");

  // Don't render if not authenticated, still loading, or user already has stores in area
  if (!userId || isInitialLoading) return null;

  const userHasStoresInArea = storeIds.some((id) =>
    nearbyStoreIds.includes(id)
  );
  if (userHasStoresInArea) return null;

  return (
    <Link
      href={browseStoresUrl}
      className="card card-border border-base-content/15 bg-base-100 h-full shadow-sm"
    >
      <div className="card-body gap-4 p-6">
        <span className="bg-primary/10 flex size-11 items-center justify-center rounded-full">
          <Store className="text-primary size-5" aria-hidden="true" />
        </span>
        <div>
          <h3 className="card-title text-lg">Join a store nearby</h3>
          <p className="text-base-content/65 mt-2 text-sm leading-relaxed sm:text-base">
            Pick a game store near you to start trading
          </p>
        </div>
        <span className="text-primary mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold">
          Get started
          <ChevronRight size={16} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}
